import { Injectable, inject } from '@angular/core';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { Store, select } from '@ngrx/store';

import { EventsKey } from 'ol/events';
import { GeoJSON } from 'ol/format';
import { Modify } from 'ol/interaction';
import { Vector as Layer } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Style, Stroke, Fill } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { take } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';

import { InteractionType } from '../../models';
import { Feature as LocalFeature } from '../../models';

import { InteractionFeatureModifying } from '../../store/actions/feature.actions';
import { getSelectedFeatures } from '../../store/selectors/feature.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoModifyService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  private modifyendEventsKey: EventsKey;
  interactionTypes = [InteractionType.modifyFeature];

  readonly source = new Vector({
    wrapX: false,
  });

  readonly layer = new Layer({
    source: this.source,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.modifyStroke,
        width: 2.5,
      }),
      fill: new Fill({
        color: MapColors.modifyFill,
      }),
    }),
  });

  readonly modify = new Modify({
    source: this.source,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.modifyStroke,
        width: 2.5,
      }),
      fill: new Fill({
        color: MapColors.modifyFill,
      }),
      image: new CircleStyle({
        radius: 7,
        stroke: new Stroke({
          color: MapColors.drawCursorStroke,
          width: 2.5,
        }),
        fill: new Fill({
          color: MapColors.drawCursorFill,
        }),
      }),
    }),
  });

  init(geoService: GeoBrowserService) {
    this.modify.setActive(false);
    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.modify);
    this.layer.setZIndex(100);
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.modify);
  }

  start(_: GeoBrowserService) {
    const writer = new GeoJSON();
    this.modify.setActive(true);
    this.layer.setVisible(true);

    this.modifyendEventsKey = this.modify.on('modifyend', (data) => {
      const featureRaw = data.features.item(0);
      if (featureRaw) {
        this.store.dispatch(
          new InteractionFeatureModifying(writer.writeFeatureObject(featureRaw) as LocalFeature),
        );
      }
    });

    this.store.pipe(select(getSelectedFeatures), take(1)).subscribe((features) => {
      features.forEach((featureEntity) => {
        const feature = writer.readFeature(featureEntity);
        feature.setId(featureEntity.id);
        this.source.addFeature(feature);
      });
    });
  }

  stop(_: GeoBrowserService) {
    this.modify.setActive(false);
    this.source.clear();
    this.layer.setVisible(false);
    unByKey(this.modifyendEventsKey);
  }
}
